//@flow
/**
 * Common PDF Generation
 * @module utils/pdf/common
 */

import pdfMake from 'pdfmake/build/pdfmake';
import 'pdfmake/build/vfs_fonts';
import moment from 'moment';
import { getBase64FromExternalImage } from 'utils/image';

import { skillNames } from 'constants';
// import { acsfLearning, acsfReading, acsfWriting, acsfOralcommunication, acsfNumeracy } from './../../assets/images/icons';
// import acsfLearning from 'assets/images/icons/acsf-learner-graph.svg';
// import acsfReading from 'assets/images/icons/acsf-reading-graph.svg';
// import acsfWriting from 'assets/images/icons/acsf-writing-graph.svg';
// import acsfOralcommunication from 'assets/images/icons/acsf-oralcommunication-graph.svg';
// import acsfNumeracy from 'assets/images/icons/acsf-numeracy-graph.svg';

import acsfLearning from 'assets/images/icons/acsf-learning.png';
import acsfReading from 'assets/images/icons/acsf-reading.png';
import acsfWriting from 'assets/images/icons/acsf-writing.png';
import acsfOralcommunication from 'assets/images/icons/acsf-oralcommunication.png';
import acsfNumeracy from 'assets/images/icons/acsf-numeracy.png';
import { store } from '../../store';

export const acsfImages = {
    learning: acsfLearning,
    reading: acsfReading,
    writing: acsfWriting,
    oralcommunication: acsfOralcommunication,
    numeracy: acsfNumeracy
};

/**
 * Creates and downloads the PDF.
 * System.import is used for code spliting the font files.
 * @memberof utils/pdf/common
 */
export async function generatePdf(docDef, fileName) {
    // const fonts = await import('./fonts.json');

    // pdfMake.vfs = fonts;
    // pdfMake.fonts = {
    //     Roboto: {
    //         normal: 'Roboto-Regular.ttf',
    //         bold: 'Roboto-Medium.ttf',
    //         italics: 'Roboto-Italic.ttf',
    //         bolditalics: 'Roboto-Italic.ttf'
    //     },
    //     FontAwesome: {
    //         normal: 'FontAwesome.ttf',
    //         bold: 'FontAwesome.ttf',
    //         italics: 'FontAwesome.ttf',
    //         bolditalics: 'FontAwesome.ttf'
    //     }
    // };

    console.log(docDef);

    return pdfMake.createPdf(docDef).download(fileName);
}

/**
 * Returns an object of pdfmake styles for uniform styling and style aliases across PDFs.
 * @memberof utils/pdf/common
 */
export function docStyles() {
    return {
        header: {
            fontSize: 26,
            bold: true,
            alignment: 'center'
        },
        subheader: {
            fontSize: 18,
            bold: true
        },
        answerHeader: {
            fontSize: 13,
            bold: true
        },
        reportItem: {
            fontSize: 10,
            margin: [0, 0, 0, 5]
        },
        tableLabel: { bold: true },
        label: {
            fontSize: 12,
            bold: true
        },
        centered: { alignment: 'center' },
        link: {
            color: 'blue',
            decoration: 'underline'
        }
    };
}

/**
 * Receives the logo, gets the dataURI for it and stores it with the key 'client-logo'.
 * It is inside an object appended to the PDF's document definition used to alias images in pdfmake.
 * @memberof utils/pdf/common
 */
export async function docImages({ absolute150: clientLogoSrc }) {
    console.log('clientLogoSrc', clientLogoSrc);
    let clientLogo = await getBase64FromExternalImage(clientLogoSrc);
    return { images: { 'client-logo': clientLogo } };
}

/**
 * Creates a pdfmake table of an ACSF profile with each skill's icon, name, and the level from the profile passed in.
 * @memberof utils/pdf/common
 */
export function acsfResultsTable(profile, testLevel) {
    const column = (skill, level) => [
        {
            width: '*',
            columns: [
                {
                    image: acsfImages[skill],
                    width: 15,
                    margin: [0, 4]
                },
                {
                    text: skillNames[skill].toString(),
                    style: 'tableLabel',
                    margin: [4, 4]
                }
            ]
        },
        {
            text: level.toString(),
            style: 'tableLabel',
            margin: [0, 4]
        }
    ];

    console.log('profile', profile);
    const columns =
        testLevel === 'acsf3shortcourse'
            ? [column('reading', profile.reading), column('writing', profile.writing), column('numeracy', profile.numeracy)]
            : [
                  column('learning', profile.learning),
                  column('reading', profile.reading),
                  column('writing', profile.writing),
                  column('oralcommunication', profile.oralcommunication),
                  column('numeracy', profile.numeracy)
              ];
    return generateTable(columns);
}

/**
 * Generates a base pdfmake document definition for uniform formatting across PDFs.
 * @memberof utils/pdf/common
 */
export async function baseDocumentDefinition() {
    let { clientSettings: settings } = store.getState().settings;
    console.log(settings);
    let logo = settings.logo || { relative: '' },
        styles = docStyles(),
        images = await docImages(logo);

    return {
        pageSize: 'A4',
        content: [],
        styles,
        ...images,
        defaultStyle: { font: 'Roboto' }
    };
}

/**
 * Generates a pdfmake table with two columns.
 * @memberof utils/pdf/common
 */
export function generateTable(rows, widths) {
    return {
        table: {
            widths: widths || ['*', '*'],
            headerRows: 1,
            body: rows
        },
        layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => 'black',
            paddingRight: () => 8,
            paddingLeft: () => 8,
            paddingTop: () => 2,
            paddingBottom: () => 2
        }
    };
}

/**
 * Generates a pdfmake table with two columns (by default) and no header.
 * Header rows are repeated each page, and we may not want that.
 *
 * Pass array to widths based on the number of columns require.  Flow doesn't like this Array<mixed>
 * but it works and the widths need to be numeric if you want to apply a fixed with
 * @memberof utils/pdf/common
 */
export function generateTableNoHeader(rows, widths) {
    return {
        table: {
            widths: widths || ['*', '*'],
            body: rows
        },
        layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => 'black',
            paddingRight: () => 8,
            paddingLeft: () => 8,
            paddingTop: () => 2,
            paddingBottom: () => 2
        }
    };
}

/**
 * Creates a pdfmake footer object for uniform styling across PDFs.
 * @memberof utils/pdf/common
 */
export function standardFooter(current, total) {
    return {
        stack: [
            horizontalRule(),
            {
                text: `Page ${current} of ${total}`,
                alignment: 'right',
                margin: [0, 10, 0, 0]
            }
        ],
        margin: [40, 0, 40, 0]
    };
}

/**
 * Creates a horizontal line for use in pdfmake.
 * The coordinates are based on an A4 page in portrait orientation.
 * @memberof utils/pdf/common
 */
export function horizontalRule(width) {
    return {
        canvas: [
            {
                type: 'line',
                x1: 0,
                y1: 5,
                x2: 595 - 2 * 40,
                y2: 5,
                lineWidth: width
            }
        ]
    };
}

/**
 * Formats a date using moment.
 * @memberof utils/pdf/common
 */
export function formatDate(date, format = 'default') {
    let formats = { default: 'ddd, D MMM YYYY, h:mm a', short: 'DD/MM/YYYY' };

    return moment(date).format(formats[format]);
}

/**
 * Using moment, formats durations of time from miliseconds to strings such as '2 minutes'.
 * NOTE: time must be passed as a number for it to be considered milliseconds
 * @memberof utils/pdf/common
 */
export function humanizeDuration(time) {
    return moment.duration(time).humanize();
}

/**
 * Recurses into a document fragment and adds margins.
 * @memberof utils/pdf/common
 */
export function addMargins(left, top, right, bottom) {
    return function addMargins(docFragment) {
        if (typeof docFragment === 'object') {
            if (Array.isArray(docFragment)) {
                docFragment = docFragment.map(addMargins);
            } else {
                docFragment.margin = [left, top, right, bottom];
            }
        }
        if (typeof docFragment === 'string') {
            docFragment = addMargins({ text: docFragment });
        }
        if (docFragment.stack) {
            docFragment.stack = docFragment.stack.map(addMargins);
        }
        if (docFragment.ul) {
            docFragment.ul = docFragment.ul.map(addMargins);
        }
        return docFragment;
    };
}
